import { z } from 'zod';
import { FeatureKey } from './features.js';
import { Priority, WorkItemType } from './work-items.js';
import { EMPTY_EMAIL_WORDING, HexColor, SupportClientEmails, SupportEmailWording } from './helpdesk.js';
import { TimeZoneName, WorkingHours } from './zones.js';
import { NotificationMatrix, NotificationType, QuietHours, mergeMatrix } from './notification-prefs.js';
import { ProviderUsername } from './repos.js';
import { COMMIT_NUDGE_DEFAULT_HOURS } from './commit-nudges.js';
import { ClientUpdateCadence } from './chapi-work.js';
import { McpTokenCreators } from './api-tokens.js';
import { TodoNudge } from './myday-repeat.js';
/**
 * Configurable everything (docs/adr/0007-settings-hierarchy.md).
 * Each setting is declared once here with its schema, default and the levels that may set it.
 * The server resolves the effective value: person → project → workspace → default.
 */
export const SettingScope = z.enum(['workspace', 'project', 'person']);
export const SettingSection = z.enum([
    'general',
    'chat',
    'tasks',
    'support',
    'time',
    'sharing',
    'chapi',
    'reminders',
    'notifications',
    'appearance',
    'security',
    'privacy',
]);
export const GroupBy = z.enum(['status', 'due', 'assignee', 'project', 'priority']);
/** Half-hour slots from 6:00 to 11:00 for the start-of-day summary. */
export const SummaryTime = z.enum([
    '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00',
]);
const Weekday = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5), z.literal(6)]);
/** The channels panel: resizable 220 to 320 px, 248 by default (design-tokens.json size.channelsPanel). */
export const CHANNELS_PANEL_WIDTH = { min: 220, max: 320, default: 248 };
export const ChannelsPanelWidth = z.number().int().min(CHANNELS_PANEL_WIDTH.min).max(CHANNELS_PANEL_WIDTH.max);
/**
 * Mini chat windows (docs/adr/0101): a conversation in a floating window at the bottom right of any page. Roomier than
 * Google Chat's by default (400 x 560), resizable from the window's top-left corner and edges between these limits, with
 * three presets in Settings > Appearance. One size for all of a person's mini windows, on every device.
 */
export const MINI_CHAT_SIZE = { minWidth: 320, minHeight: 400, maxWidth: 960, maxHeight: 1200 };
export const MINI_CHAT_PRESETS = {
    compact: { width: 340, height: 460 },
    comfortable: { width: 400, height: 560 },
    large: { width: 480, height: 680 },
};
export const MiniChatSize = z.object({
    width: z.number().int().min(MINI_CHAT_SIZE.minWidth).max(MINI_CHAT_SIZE.maxWidth),
    height: z.number().int().min(MINI_CHAT_SIZE.minHeight).max(MINI_CHAT_SIZE.maxHeight),
});
/** Message and row density (v4 RESTYLE_BRIEF "Density setting"; design-tokens.json `density`). Comfortable is the default. */
export const DENSITIES = ['comfortable', 'compact'];
export const Density = z.enum(DENSITIES);
/** What lives under the rail's More (UI-19) and may be pinned onto the rail. */
export const RailPinKey = z.enum(['time', 'invoices', 'reports', 'tasks', 'search']);
const SlaPolicy = z.object({ responseHours: z.number().positive(), resolveHours: z.number().positive() });
export const SlaPolicies = z.record(Priority, SlaPolicy);
const s = (d) => d;
export const SETTINGS = {
    'general.timezone': s({
        schema: TimeZoneName,
        default: 'Australia/Sydney',
        scopes: ['workspace', 'person'],
        section: 'general',
        label: 'Time zone',
        description: 'Used for due dates, reminders and the start of the day, and shown as local time on profiles.',
    }),
    // Profiles and local time (docs/adr/0032-profiles-and-local-time.md). A person's own values are edited
    // on Your account > Profile; the workspace value is the default for people who have not chosen.
    'general.timezoneAuto': s({
        schema: z.boolean(),
        default: true,
        scopes: ['workspace', 'person'],
        section: 'general',
        label: 'Set time zones from each device',
        description: "The web and phone apps keep a person's time zone in step with their device until they pick one themselves.",
    }),
    'general.workingHours': s({
        schema: WorkingHours.nullable(),
        default: null,
        scopes: ['person'],
        section: 'general',
        label: 'Working hours',
        description: 'When you usually work, in your own time zone. Others see "Outside working hours" on your profile.',
        ownScreen: true,
    }),
    'general.weekStartsOn': s({
        schema: z.union([z.literal(0), z.literal(1)]),
        default: 1,
        scopes: ['workspace'],
        section: 'general',
        label: 'Week starts on',
        description: 'Monday or Sunday.',
    }),
    'general.clientLabel': s({
        schema: z.string().min(1).max(24),
        default: 'Client',
        scopes: ['workspace'],
        section: 'general',
        label: 'What you call a client',
        description: 'Some teams say Account, Customer or Department.',
    }),
    // Every project has its own channel since docs/adr/0074 (on for every workspace); kept readable for older apps. No
    // longer offered in Settings → Features (Nathan, 20 Oct 2026): it controls nothing, so it has no screen.
    'chat.channelPerProject': s({
        schema: z.boolean(),
        default: true,
        scopes: ['workspace'],
        section: 'chat',
        label: 'Separate channel per project',
        description: 'Each project has its own channel, alongside its client’s general channel.',
        ownScreen: true,
    }),
    'chat.whoCanCreateChannels': s({
        schema: z.enum(['members', 'admins']),
        // Owners and admins set channels up and add people; members start DMs and group chats (Nathan, 22 Oct 2026).
        default: 'admins',
        scopes: ['workspace'],
        section: 'chat',
        label: 'Who can create channels',
        description: 'Members: owners, admins and members. Admins: owners and admins only. Guests never can.',
    }),
    // Who adds teammates to a private channel (docs/adr/0099, Nathan 28 Oct 2026). Only the workspace's own people; people
    // from outside stay with owners and admins whatever this says. Guests never add anyone.
    'chat.whoCanAddToPrivateChannels': s({
        schema: z.enum(['members', 'creator', 'admins']),
        default: 'members',
        scopes: ['workspace'],
        section: 'chat',
        label: 'Who can add people to private channels',
        description: 'Teammates from the workspace only. People from outside are always added by owners and admins, and guests never add anyone.',
    }),
    'chat.gifs': s({
        schema: z.enum(['off', 'g', 'pg', 'pg-13']),
        default: 'pg',
        scopes: ['workspace'],
        section: 'chat',
        label: 'GIFs',
        description: 'GIFs from GIPHY in chat, up to this content rating. G suits everyone; PG-13 allows mild language and suggestive content.',
    }),
    'chat.clientsInCatchUps': s({
        schema: z.boolean(),
        default: false,
        scopes: ['workspace'],
        section: 'chat',
        label: 'Allow clients in catch-ups',
        description: 'Client contacts can join catch-ups in the channels they share with you. Off: they see that one is happening, but cannot join.',
        feature: 'catchups',
    }),
    // Check before posting where clients read (docs/adr/0066-client-post-check.md). Runs on the device; nothing is sent.
    'chat.clientPostCheck': s({
        schema: z.boolean(),
        default: true,
        scopes: ['workspace'],
        section: 'chat',
        label: 'Check messages before they reach clients',
        description: 'Before someone on the team sends a message clients can read, they see a check if it looks rude or holds a password, card or bank number, or a link to something internal. The check runs on their device; the message is not sent anywhere else.',
    }),
    'chat.clientPostCheckMine': s({
        schema: z.boolean(),
        default: true,
        scopes: ['person'],
        section: 'chat',
        label: 'Check my messages before they reach clients',
        description: 'Ask before sending something that looks rude or sensitive in a channel clients can read. Only while the workspace has the check on.',
    }),
    'chat.guestsCanDm': s({
        schema: z.boolean(),
        default: false,
        scopes: ['workspace'],
        section: 'chat',
        label: 'Guests can send direct messages',
        description: 'Client contacts can message your team directly, not only in shared channels.',
    }),
    'tasks.enabledTypes': s({
        schema: z.array(WorkItemType).min(1),
        default: ['task', 'job', 'support', 'bug'],
        scopes: ['workspace'],
        section: 'tasks',
        label: 'Work item types',
        description: 'Job (fixed-price) and Bug are optional. Support needs the Support desk feature.',
        feature: 'tasks',
    }),
    'tasks.allTasksView': s({
        schema: z.boolean(),
        default: true,
        scopes: ['workspace'],
        section: 'tasks',
        label: 'All tasks view',
        description: 'Adds "All tasks" to the sidebar: every task across every project on one screen, grouped by due date.',
        feature: 'tasks',
    }),
    'tasks.groupByDueDate': s({
        schema: z.boolean(),
        default: true,
        scopes: ['workspace'],
        section: 'tasks',
        label: 'Group by due date',
        description: 'Lets the List view group work into Overdue, Today, This week, Later and No date.',
        feature: 'tasks',
    }),
    'tasks.defaultGroupBy': s({
        schema: GroupBy,
        default: 'status',
        scopes: ['workspace', 'project', 'person'],
        section: 'tasks',
        label: 'Default grouping in List view',
        description: 'How the List view is grouped when you open it.',
        feature: 'tasks',
    }),
    // The task fly-out (docs/adr/0092): where a comment goes unless the person picks otherwise, and whether it opens full screen.
    'tasks.commentDestination': s({
        schema: z.enum(['both', 'task']),
        default: 'both',
        scopes: ['workspace', 'project'],
        section: 'tasks',
        label: 'Task comments go to',
        description: 'Both: a comment on a task is also posted under the task in the project chat. Task: comments stay private to the task. Each comment can still choose.',
        feature: 'tasks',
    }),
    'tasks.drawerFullScreen': s({
        schema: z.boolean(),
        default: false,
        scopes: ['person'],
        section: 'tasks',
        label: 'Open tasks full screen',
        description: 'The task fly-out fills the content area instead of sliding in at the side. The fly-out\'s own button changes this too.',
        feature: 'tasks',
    }),
    'support.drawerLarge': s({
        schema: z.boolean(),
        default: false,
        scopes: ['person'],
        section: 'tasks',
        label: 'Open tickets large',
        description: 'A support ticket opens across three quarters of the screen instead of the narrow fly-out. The ticket\'s own enlarge button changes this too.',
        feature: 'support',
    }),
    'tasks.postStatusChangesToChat': s({
        schema: z.boolean(),
        default: true,
        scopes: ['workspace', 'project'],
        section: 'tasks',
        label: 'Post status changes to chat',
        description: 'Moving a card posts a short line in the project chat so the conversation stays the record.',
        feature: 'tasks',
    }),
    // Repositories (docs/adr/0052). Off by default: a merge only moves a task when the workspace asks for it.
    // Per project too (docs/adr/0059): a project's own rule wins over the workspace's.
    'repos.mergedPullRequestMovesTo': s({
        schema: z.enum(['off', 'review', 'done']),
        default: 'off',
        scopes: ['workspace', 'project'],
        section: 'tasks',
        label: 'When a linked pull request is merged',
        description: 'Off leaves the task where it is. Review or Done moves a linked task there when its pull request is merged.',
        feature: 'repos',
    }),
    // Commit nudges (docs/adr/0059). Private to the assignee; the lead only when a project names one.
    'repos.commitNudgeAfterHours': s({
        schema: z.number().int().min(0).max(168),
        default: COMMIT_NUDGE_DEFAULT_HOURS,
        scopes: ['workspace', 'project'],
        section: 'reminders',
        label: 'Nudge the assignee when nothing is pushed for',
        description: 'Working hours an In progress task can go with time logged or a status change but no linked commit, before Chapi nudges its assignee privately. Each project can set its own. 0 turns it off.',
        feature: 'repos',
    }),
    'repos.commitNudgeLeadUserId': s({
        schema: z.uuid().nullable(),
        default: null,
        scopes: ['project'],
        section: 'reminders',
        label: 'Also nudge the project lead',
        description: 'Who hears about it when a second full threshold passes with still no commit. Off unless chosen.',
        feature: 'repos',
        customUi: true,
    }),
    'repos.endOfDayNudge': s({
        schema: z.boolean(),
        default: false,
        scopes: ['person'],
        section: 'reminders',
        label: 'Nudge me at the end of my day if nothing is pushed',
        description: 'When your working day ends and none of your In progress tasks has a commit or pull request linked since it began.',
        feature: 'repos',
    }),
    'repos.usernames': s({
        schema: z.array(ProviderUsername).max(5),
        default: [],
        scopes: ['person'],
        section: 'tasks',
        label: 'Your usernames on GitHub, GitLab or Bitbucket',
        description: 'Pull requests that ask you for a review show on My work. Nobody else sees these.',
        feature: 'repos',
    }),
    'support.inboundEmail': s({
        schema: z.boolean(),
        default: true,
        scopes: ['workspace'],
        section: 'support',
        label: 'Turn email into tickets',
        description: 'Mail to the workspace support address becomes a ticket, and replies come back onto it.',
        feature: 'support',
        customUi: true,
    }),
    'support.inboxProjectId': s({
        schema: z.uuid().nullable(),
        default: null,
        scopes: ['workspace'],
        section: 'support',
        label: 'Support inbox project',
        description: 'Where tickets go when the sender does not match a client.',
        feature: 'support',
        customUi: true,
    }),
    'support.autoAcknowledge': s({
        schema: z.boolean(),
        default: true,
        scopes: ['workspace'],
        section: 'support',
        label: 'Acknowledge new tickets by email',
        description: 'The customer gets a short reply with their ticket number, so they know it arrived.',
        feature: 'support',
    }),
    'support.reopenWithinDays': s({
        schema: z.number().int().min(0).max(90),
        default: 7,
        scopes: ['workspace'],
        section: 'support',
        label: 'Reopen a resolved ticket when the customer replies within',
        description: 'Days. After that, a reply starts a new ticket. 0 always starts a new one.',
        feature: 'support',
    }),
    'support.slaPolicies': s({
        schema: SlaPolicies,
        default: {
            p1: { responseHours: 1, resolveHours: 4 },
            p2: { responseHours: 4, resolveHours: 24 },
            p3: { responseHours: 8, resolveHours: 72 },
            p4: { responseHours: 24, resolveHours: 120 },
        },
        scopes: ['workspace', 'project'],
        section: 'support',
        label: 'SLA policy',
        description: 'First response and resolution targets by priority, in working hours.',
        feature: 'support',
    }),
    'support.brandColor': s({
        schema: HexColor,
        default: '#6D28D9',
        scopes: ['workspace'],
        section: 'support',
        label: 'Brand colour',
        description: 'Used on the emails clients get and in the client portal.',
        feature: 'support',
        customUi: true,
    }),
    'support.clientEmails': s({
        schema: SupportClientEmails,
        default: { received: true, reply: true, waiting: true, resolved: true, hours: true, statement: true },
        scopes: ['workspace'],
        section: 'support',
        label: 'Emails to clients',
        description: 'Which emails clients get about their tickets and support plan.',
        feature: 'support',
        customUi: true,
    }),
    'support.emailWording': s({
        schema: SupportEmailWording,
        default: EMPTY_EMAIL_WORDING,
        scopes: ['workspace'],
        section: 'support',
        label: 'Wording of client emails',
        description: 'Your own heading and message for some client emails, and a sign-off on all of them.',
        feature: 'support',
        customUi: true,
    }),
    'time.roundingMinutes': s({
        schema: z.union([z.literal(1), z.literal(5), z.literal(6), z.literal(10), z.literal(15), z.literal(30)]),
        default: 15,
        scopes: ['workspace'],
        section: 'time',
        label: 'Round logged time to',
        description: 'Minutes. Applied when time is logged.',
        feature: 'time',
    }),
    'time.weeklyCapacityHours': s({
        schema: z.number().min(1).max(80),
        default: 30,
        scopes: ['workspace', 'person'],
        section: 'time',
        label: 'Weekly capacity',
        description: 'Billable hours a person aims for each week. Shown on My work.',
        feature: 'time',
    }),
    // Timesheets and retainers (docs/adr/0042-timesheets-and-retainers.md).
    'time.clientReports': s({
        schema: z.boolean(),
        default: false,
        scopes: ['workspace', 'project'],
        section: 'time',
        label: 'Monthly reports in the client portal',
        description: "The client sees this project's monthly hours report and retainer in the portal: client-visible tasks by name, other work as one line.",
        feature: 'time',
    }),
    // Invoicing (docs/adr/0054): off by default, and then every entry counts as billable and nothing asks.
    'time.trackBillable': s({
        schema: z.boolean(),
        default: false,
        scopes: ['workspace'],
        section: 'time',
        label: 'Track billable and non-billable time',
        description: 'Time logs get a Billable switch (on by default). Reports split the two, and invoices bill only billable hours.',
        feature: 'time',
    }),
    'time.monthlyReportEmail': s({
        schema: z.boolean(),
        default: false,
        scopes: ['workspace'],
        section: 'time',
        label: 'Email clients their monthly report',
        description: "On the 1st, send each client's billing contacts a link to last month's report in the portal. Needs the client portal.",
        feature: 'time',
    }),
    'sharing.linksEnabled': s({
        schema: z.boolean(),
        default: true,
        scopes: ['workspace'],
        section: 'sharing',
        label: 'Share links',
        description: 'Members can share a read-only link to a task or ticket with someone outside the workspace.',
    }),
    'sharing.defaultExpiryDays': s({
        schema: z.number().int().min(1).max(365),
        default: 30,
        scopes: ['workspace'],
        section: 'sharing',
        label: 'Links expire after',
        description: 'Days. Every link expires; owners and admins can revoke any link sooner.',
    }),
    'sharing.whoCanShare': s({
        schema: z.enum(['admins', 'members']),
        default: 'members',
        scopes: ['workspace'],
        section: 'sharing',
        label: 'Who can create share links',
        description: 'Guests can never create share links.',
    }),
    'chapi.excludePrivate': s({
        schema: z.boolean(),
        default: true,
        scopes: ['workspace'],
        section: 'chapi',
        label: 'Keep Chapi out of private channels and DMs',
        description: 'Chapi only ever reads what the person asking can read. This also keeps it out of private conversations.',
        feature: 'chapi',
    }),
    'chapi.modelPath': s({
        schema: z.enum(['australia_only', 'anthropic_zdr']),
        default: 'australia_only',
        scopes: ['workspace'],
        section: 'chapi',
        label: 'Where Chapi runs',
        description: 'Australia only keeps every request in Sydney and Melbourne. The alternative uses the Anthropic API with zero data retention.',
        feature: 'chapi',
    }),
    'chapi.monthlyTokenBudget': s({
        schema: z.number().int().min(0).max(500_000_000),
        default: 5_000_000,
        scopes: ['workspace'],
        section: 'chapi',
        label: 'Monthly allowance',
        description: 'Tokens Chapi may use each month across the workspace (about US$20 at the default). Chapi pauses when it is used up.',
        feature: 'chapi',
    }),
    /** Showcase phase B (docs/adr/0067): the person's "Hide Chapi" in the design editor, remembered on every device. */
    'showcase.hideChapi': s({
        schema: z.boolean(),
        default: false,
        scopes: ['person'],
        section: 'chapi',
        label: 'Hide Chapi in the design editor',
        description: 'Hides the Chapi panel in Showcase. Everything Chapi does can also be done by hand.',
        feature: 'showcase',
        ownScreen: true,
    }),
    'chapi.morningSummary': s({
        schema: z.boolean(),
        default: true,
        scopes: ['workspace', 'person'],
        section: 'chapi',
        label: 'Morning summary on My work',
        description: 'Chapi summarises what happened overnight and what needs you today.',
        feature: 'chapi',
    }),
    // Spotting tasks in chat (docs/adr/0079): rules only, shown only to the message's author.
    'chapi.taskSuggestions': s({
        schema: z.boolean(),
        default: true,
        scopes: ['workspace', 'person'],
        section: 'chapi',
        label: 'Spot tasks in my messages',
        description: 'When you write something like "I\'ll fix the refund bug tomorrow", Chapi offers (only to you) to make it a task.',
        feature: 'chapi',
    }),
    // Client progress updates (docs/adr/0063): Chapi drafts one on this cadence; a person always reviews and sends it.
    'chapi.clientUpdates': s({
        schema: ClientUpdateCadence,
        default: 'off',
        scopes: ['project'],
        section: 'chapi',
        label: 'Draft a client update',
        description: 'Chapi drafts a progress update for the client from what moved. Someone on the team reviews, edits and sends it; nothing goes out on its own.',
        feature: 'chapi',
        ownScreen: true,
    }),
    'reminders.dailySummary': s({
        schema: z.boolean(),
        default: true,
        scopes: ['workspace', 'person'],
        section: 'reminders',
        label: 'Start-of-day summary',
        description: 'One note each working morning: what is overdue, due today or has gone quiet. Never one nudge per task.',
    }),
    'reminders.summaryTime': s({
        schema: SummaryTime,
        default: '08:30',
        scopes: ['workspace', 'person'],
        section: 'reminders',
        label: 'Send the summary at',
        description: 'In your own time zone.',
    }),
    'reminders.workingDays': s({
        schema: z.array(Weekday).max(7),
        default: [1, 2, 3, 4, 5],
        scopes: ['workspace', 'person'],
        section: 'reminders',
        label: 'Working days',
        description: 'Summaries and safety nets only arrive on these days. Reminders you set yourself always arrive.',
    }),
    'reminders.staleAfterDays': s({
        schema: z.number().int().min(0).max(30),
        default: 3,
        scopes: ['workspace', 'project'],
        section: 'reminders',
        label: 'Flag in-progress work as gone quiet after',
        description: 'Days with no update, comment or time logged. 0 turns it off.',
        feature: 'tasks',
    }),
    'reminders.unownedAfterHours': s({
        schema: z.number().int().min(0).max(168),
        default: 24,
        scopes: ['workspace'],
        section: 'reminders',
        label: 'Tell admins about work with no owner after',
        description: 'Hours an open item can go without an assignee or due date. 0 turns it off.',
        feature: 'tasks',
    }),
    'reminders.slaWarningMinutes': s({
        schema: z.number().int().min(0).max(480),
        default: 60,
        scopes: ['workspace'],
        section: 'reminders',
        label: 'Warn the assignee before a ticket breaches its SLA',
        description: 'Minutes before the deadline. 0 turns it off.',
        feature: 'support',
    }),
    // My day (docs/adr/0107): the nudge an item with a time gets unless it says otherwise.
    'reminders.myDayNudge': s({
        schema: TodoNudge,
        default: 'at',
        scopes: ['person'],
        section: 'reminders',
        label: 'Nudge me about My day items with a time',
        description: 'The default for each to-do with a time. Each to-do can change it.',
        feature: 'todo',
    }),
    // Appearance (docs/designs/mobile/CHAT_CHANGES.md, "Appearance setting"). Per person; the workspace
    // value is the default for everyone who has not chosen. Edited on Settings > Appearance.
    'appearance.style': s({
        schema: z.enum(['calm', 'vibrant']),
        default: 'calm',
        scopes: ['workspace', 'person'],
        section: 'appearance',
        label: 'Style',
        description: 'Calm uses colour for meaning; Vibrant adds bolder tiles and icons.',
        ownScreen: true,
    }),
    'appearance.accent': s({
        schema: z.enum(['violet', 'ocean', 'teal', 'sunset', 'rose', 'graphite']),
        default: 'violet',
        scopes: ['workspace', 'person'],
        section: 'appearance',
        label: 'Main colour',
        description: 'Drives the top bar, primary buttons, active tab, unread badges and links. Status, priority and SLA colours never change.',
        ownScreen: true,
    }),
    'appearance.peopleRow': s({
        schema: z.boolean(),
        default: true,
        scopes: ['workspace', 'person'],
        section: 'appearance',
        label: 'People row in Chat',
        description: 'Recent people and their status circles.',
        ownScreen: true,
    }),
    'appearance.peopleRowOrder': s({
        schema: z.enum(['recent', 'frequent']),
        default: 'recent',
        scopes: ['workspace', 'person'],
        section: 'appearance',
        label: 'People row order',
        description: 'The people you message most recently, or most often.',
        ownScreen: true,
    }),
    'appearance.darkMode': s({
        schema: z.enum(['system', 'light', 'dark']),
        default: 'system',
        scopes: ['workspace', 'person'],
        section: 'appearance',
        label: 'Dark mode',
        description: 'Follow the device, or always light or dark.',
        ownScreen: true,
    }),
    'appearance.textSize': s({
        schema: z.enum(['small', 'default', 'large', 'larger']),
        default: 'default',
        scopes: ['workspace', 'person'],
        section: 'appearance',
        label: 'Text size',
        description: 'Scales all text in the app.',
        ownScreen: true,
    }),
    // The composer's formatting bar (Nathan, 20 Oct 2026): shown or hidden with its Aa button, remembered for the
    // person on every device and in every workspace (docs/adr/0075).
    'appearance.formattingBar': s({
        schema: z.boolean(),
        default: false,
        scopes: ['person'],
        section: 'appearance',
        label: 'Formatting bar',
        description: 'Bold, italic, lists, links and code buttons above the message box.',
        ownScreen: true,
    }),
    // The v4 shell (docs/designs/v4/RESTYLE_BRIEF.md 3 and 4, docs/adr/0082): the channels panel's width and the items a
    // person pinned from More onto the rail. Their own, on every device and in every workspace (docs/adr/0075); a pinned
    // item the person cannot use in a workspace (its feature off, or their role) is simply not shown there.
    'appearance.channelsPanelWidth': s({
        schema: ChannelsPanelWidth,
        default: CHANNELS_PANEL_WIDTH.default,
        scopes: ['person'],
        section: 'appearance',
        label: 'Channels panel width',
        description: 'Drag the edge of the channels panel to change it.',
        ownScreen: true,
    }),
    // Mini chat windows (docs/adr/0101): their size, set by dragging a window's top-left corner or edges, or by a preset.
    'appearance.miniChatSize': s({
        schema: MiniChatSize,
        default: { width: MINI_CHAT_PRESETS.comfortable.width, height: MINI_CHAT_PRESETS.comfortable.height },
        scopes: ['person'],
        section: 'appearance',
        label: 'Mini chat window size',
        description: 'Compact, Comfortable or Large, or drag a mini window\'s top-left corner.',
        ownScreen: true,
    }),
    // Density (v4 RESTYLE_BRIEF "Density setting", Prompt 9): Comfortable (28 px rows, 14/21 messages, 36 px avatars) or
    // Compact (24 px rows, 13/19 messages, 4 px group padding, 28 px avatars). The person's own, on every device (0075).
    'appearance.density': s({
        schema: Density,
        default: 'comfortable',
        scopes: ['person'],
        section: 'appearance',
        label: 'Density',
        description: 'Compact fits more messages and rows on the screen.',
        ownScreen: true,
    }),
    'appearance.railPins': s({
        schema: z.array(RailPinKey).max(RailPinKey.options.length).refine((a) => new Set(a).size === a.length, 'Each item once'),
        default: [],
        scopes: ['person'],
        section: 'appearance',
        label: 'Pinned to the rail',
        description: 'Items from More you dragged onto the rail.',
        ownScreen: true,
    }),
    // Superseded by the daily-summary email cell of `notifications.matrix` (docs/adr/0048); still read when that cell is unset.
    'notifications.summaryEmail': s({
        schema: z.boolean(),
        default: true,
        scopes: ['person'],
        section: 'notifications',
        label: 'Email me the start-of-day summary',
        description: 'It also appears in your Inbox either way.',
        ownScreen: true,
    }),
    // Superseded by the push column of `notifications.matrix` and this device's browser permission (docs/adr/0048).
    'notifications.desktop': s({
        schema: z.boolean(),
        default: false,
        scopes: ['person'],
        section: 'notifications',
        label: 'Desktop notifications',
        description: 'Show mentions, replies and reminders as browser notifications while Chapsi is open in the background.',
        ownScreen: true,
    }),
    // Notification preferences (docs/adr/0048): edited on Notifications in Your account and Workspace settings.
    'notifications.matrix': s({
        schema: NotificationMatrix,
        default: {},
        scopes: ['workspace', 'person'],
        section: 'notifications',
        label: 'What reaches you, and where',
        description: 'For each kind of notification: in the app, as a push to your phone, desktop or browser, and by email.',
        ownScreen: true,
        merge: 'cells',
    }),
    'notifications.disabledTypes': s({
        schema: z.array(NotificationType).max(NotificationType.options.length),
        default: [],
        scopes: ['workspace'],
        section: 'notifications',
        label: 'Switched off for everyone',
        description: 'Nobody in the workspace gets these, whatever they choose.',
        ownScreen: true,
    }),
    'notifications.quietHours': s({
        schema: QuietHours.nullable(),
        default: null,
        scopes: ['person'],
        section: 'notifications',
        label: 'Quiet hours',
        description: 'No push or email during these hours in your time zone. Your inbox still fills.',
        ownScreen: true,
    }),
    'notifications.channelDefault': s({
        schema: z.enum(['all', 'mentions', 'none']),
        default: 'all',
        scopes: ['person'],
        section: 'notifications',
        label: 'Notify me about',
        description: 'Default for channels you join. Each channel can override it.',
    }),
    'notifications.unreadEmailAfterMinutes': s({
        schema: z.number().int().min(0).max(1440),
        default: 180,
        scopes: ['workspace', 'person'],
        section: 'notifications',
        label: 'Email unread messages after',
        description: 'Minutes. 0 turns the email off.',
    }),
    // Sounds (docs/designs/v5-chat-projects/REWORK_BRIEF.md "Sound", docs/adr/0095): the one chime, on or off. No tone
    // picker. Edited on Settings → Notifications with quiet hours and do not disturb.
    'notifications.sound': s({
        schema: z.boolean(),
        default: true,
        scopes: ['person'],
        section: 'notifications',
        label: 'Sounds',
        description: 'A short chime for a new message, a mention or direct message, a task assigned to you, a comment on your task, and when you mark a task done. Never in quiet hours, while paused, or in a catch-up.',
        ownScreen: true,
    }),
    /**
     * Who may make personal API tokens for the Claude connector (docs/adr/0098): `admins` (owners and admins) or `staff`
     * (owners, admins and members). Guests never. Set in Settings → Connectors; tokens already made keep working.
     */
    'mcp.tokenCreators': s({
        schema: McpTokenCreators,
        default: 'staff',
        scopes: ['workspace'],
        section: 'security',
        label: 'Who can make tokens',
        description: 'Owners and admins only, or all staff (owners, admins and members). Guests never can.',
        feature: 'mcp',
        ownScreen: true,
    }),
    /**
     * "Require two-factor sign-in" (docs/adr/0033). off: nobody is made to; admins: owners and admins;
     * members: everyone but guests; everyone: guests too. Shown on its own card in Settings → Security.
     */
    'security.requireMfa': s({
        schema: z.enum(['off', 'admins', 'members', 'everyone']),
        default: 'off',
        scopes: ['workspace'],
        section: 'security',
        label: 'Require two-factor sign-in',
        description: 'People without a passkey or authenticator app set one up at their next sign-in before using this workspace.',
        ownScreen: true,
    }),
    'security.sessionIdleDays': s({
        schema: z.number().int().min(1).max(90),
        default: 30,
        scopes: ['workspace'],
        section: 'security',
        label: 'Sign out after inactivity',
        description: 'Days without use before a device must sign in again.',
    }),
    'security.sessionAbsoluteDays': s({
        schema: z.number().int().min(1).max(365),
        default: 90,
        scopes: ['workspace'],
        section: 'security',
        label: 'Maximum session length',
        description: 'Days. Every device signs in again after this, active or not.',
    }),
    'security.allowedEmailDomains': s({
        schema: z.array(z.string().regex(/^[a-z0-9.-]+\.[a-z]{2,}$/)).max(20),
        default: [],
        scopes: ['workspace'],
        section: 'security',
        label: 'Allowed email domains for members',
        description: 'Leave empty to allow any. Guests are not limited.',
    }),
    'files.maxUploadMb': s({
        schema: z.number().int().min(1).max(200),
        default: 50,
        scopes: ['workspace'],
        section: 'chat',
        label: 'Largest file people can upload',
        description: 'Megabytes per file, for chat, tasks and tickets.',
    }),
    'privacy.showLocalTime': s({
        schema: z.boolean(),
        default: true,
        scopes: ['workspace'],
        section: 'privacy',
        label: "Show people's local time",
        description: 'On profiles and in direct messages, with working hours. Off hides everyone\'s time zone from everyone else.',
    }),
    // Birthdays (docs/adr/0047-birthdays-and-contact-visibility.md). Off until an owner or admin turns it on.
    'privacy.birthdays': s({
        schema: z.boolean(),
        default: false,
        scopes: ['workspace'],
        section: 'privacy',
        label: 'Birthday celebrations',
        description: "On someone's birthday, teammates see a card on My work with a nudge to send them a happy birthday. People add a day and month (never a year) on their profile and can choose not to be celebrated. Client guests never see birthdays.",
    }),
    'search.byMeaning': s({
        schema: z.boolean(),
        default: true,
        scopes: ['workspace'],
        section: 'privacy',
        label: 'Search by meaning',
        description: "Also finds messages, tasks and tickets that use different words for the same thing. Amazon Bedrock in your workspace's region turns text into numbers that stay in your workspace; people only ever see results they could already open. Channels where Chapi is switched off are left out.",
    }),
    // Reports (docs/adr/0053-reports.md): without it, members see only their own hours and work.
    'reports.memberTeamView': s({
        schema: z.boolean(),
        default: false,
        scopes: ['workspace'],
        section: 'privacy',
        label: 'Members see team reports',
        description: "Members see everyone's hours, retainers, throughput, support times and workload in Reports. Off: members see only their own hours and work there. Owners and admins always see everything; guests never see Reports.",
        feature: 'reports',
    }),
    'privacy.deletedRetentionDays': s({
        schema: z.number().int().min(0).max(90),
        default: 30,
        scopes: ['workspace'],
        section: 'privacy',
        label: 'Keep deleted items for',
        description: 'Days before deleted messages and tasks are permanently removed.',
    }),
};
export const SettingKeySchema = z.enum(Object.keys(SETTINGS));
export function defaultSettings() {
    return Object.fromEntries(Object.entries(SETTINGS).map(([k, d]) => [k, d.default]));
}
/** Most specific valid value wins; invalid stored values fall back silently to the next layer. */
export function resolveSettings(layers) {
    const out = defaultSettings();
    for (const key of Object.keys(SETTINGS)) {
        const def = SETTINGS[key];
        for (const scope of ['workspace', 'project', 'person']) {
            if (!def.scopes.includes(scope))
                continue;
            const raw = layers[scope]?.[key];
            if (raw === undefined)
                continue;
            const parsed = def.schema.safeParse(raw);
            if (!parsed.success)
                continue;
            out[key] = 'merge' in def && def.merge === 'cells' ? mergeMatrix(out[key], parsed.data) : parsed.data;
        }
    }
    return out;
}
export const SettingsValues = z.record(z.string(), z.unknown());
/** Validates a partial update for one scope. Returns per-key errors. */
export function validateSettingsUpdate(scope, values, 
/** Settings with their own screen are changed only through their own route, which checks them. */
opts = {}) {
    const errors = [];
    const clean = {};
    for (const [key, value] of Object.entries(values)) {
        const def = SETTINGS[key];
        if (!def) {
            errors.push({ path: key, message: 'Unknown setting' });
            continue;
        }
        if (!def.scopes.includes(scope)) {
            errors.push({ path: key, message: `Cannot be set at ${scope} level` });
            continue;
        }
        if (def.customUi && !opts.allowCustomUi) {
            errors.push({ path: key, message: 'Change this on its own settings screen' });
            continue;
        }
        if (value === null) {
            clean[key] = null; // null clears the override
            continue;
        }
        const parsed = def.schema.safeParse(value);
        if (!parsed.success)
            errors.push({ path: key, message: parsed.error.issues[0]?.message ?? 'Invalid' });
        else
            clean[key] = parsed.data;
    }
    return errors.length ? { ok: false, errors } : { ok: true, values: clean };
}
export const SettingDescriptor = z.object({
    key: z.string(),
    section: SettingSection,
    label: z.string(),
    description: z.string(),
    scopes: z.array(SettingScope),
    feature: FeatureKey.nullable(),
    default: z.unknown(),
});
/** Settings shown in the generated settings forms (not those edited on their own screen). */
/**
 * The workspace settings a guest (client contact) needs to use the app (security review follow-up, 16 Oct 2026):
 * time and week display, what the workspace calls a client, upload size, GIFs, whether they may DM, which work
 * types exist, whether local times show and which notification types are off. Guests also read every setting a person can set for themselves
 * (their own theme, notifications and so on). Everything else (support mailboxes and wording, SLA policies,
 * security policy, Chapi, sharing, reminders, time rules, retention) is staff-only and never sent to them.
 */
export const GUEST_WORKSPACE_SETTING_KEYS = [
    'general.timezone', 'general.weekStartsOn', 'general.clientLabel',
    'chat.gifs', 'chat.guestsCanDm', 'tasks.enabledTypes', 'files.maxUploadMb', 'privacy.showLocalTime',
    // Which notification types the workspace switched off, so a guest's own Notifications screen shows them off (docs/adr/0048).
    'notifications.disabledTypes',
    // Whether they may join catch-ups, so the app shows Join or a note instead (docs/adr/0057).
    'chat.clientsInCatchUps',
];
/**
 * Person settings that belong to the person in every workspace (docs/adr/0075): their look. A person's own value is
 * kept once for them (user_preferences) and follows them into every workspace and onto every device; the workspace
 * value stays the default for people who have not chosen. Every other person setting is kept per workspace.
 */
export function followsPersonEverywhere(key) {
    return key.startsWith('appearance.') && key in SETTINGS;
}
/** Whether a guest may read this setting's value: on the list above, or one they can set for themselves. */
export function guestCanReadSetting(key) {
    if (GUEST_WORKSPACE_SETTING_KEYS.includes(key))
        return true;
    const def = SETTINGS[key];
    return !!def && def.scopes.includes('person');
}
export function settingDescriptors() {
    return Object.entries(SETTINGS)
        .filter(([, d]) => !('customUi' in d && d.customUi) && !('ownScreen' in d && d.ownScreen))
        .map(([key, d]) => ({
        key,
        section: d.section,
        label: d.label,
        description: d.description,
        scopes: [...d.scopes],
        feature: 'feature' in d ? d.feature : null,
        default: d.default,
    }));
}
export const SettingsResponse = z.object({
    /** Effective values for the caller (all layers resolved). */
    effective: z.record(z.string(), z.unknown()),
    /** Raw values stored at the requested scope (only overrides). */
    stored: z.record(z.string(), z.unknown()),
    definitions: z.array(SettingDescriptor),
});
export const UpdateSettingsInput = z.object({ values: SettingsValues });
//# sourceMappingURL=settings.js.map